import type { BulkPriceLine, Item, ItemPurchaseContext, ItemUnit, Party, PartyPricingBasis, Warehouse } from '@erp/shared';
import Decimal from 'decimal.js';
import { ExternalLink, ScanLine, Tags, Ticket, Wallet, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import {
  Button, Card, Combobox, type ComboboxOption, confirmDialog, FormField, Input, NumberInput, Skeleton,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { cn } from '../../lib/cn.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';
import { getPartyPricingBasis, resolvePrice, resolvePricesBulk } from '../../lib/pricing.js';

import { type BlockingPosSale, blockingPosSale, reviseErrorMessage } from './reviseErrors.js';

type PartySide = 'customer' | 'vendor';

export interface DocumentFormPageProps {
  /** API path under /api/v1, e.g. "/invoices". */
  endpoint: string;
  /** Translation namespace key, e.g. "invoices". */
  ns: string;
  /** Filter party list by type. Customer for SO/Invoice; vendor for PO/Bill. */
  partySide: PartySide;
  /** True for invoices and bills — adds a due-date field. */
  hasDueDate: boolean;
  /** Override for the FIRST date's label (bills call it the purchase date). */
  docDateLabelKey?: string;
  /** Override the due-date field label key (e.g. 'form.validUntil' for quotes). */
  dueDateLabelKey?: string;
  /** After successful create, redirect here (e.g. "/sales/invoices"). */
  listPath: string;
  /** When true, hydrate from `:id` route param and PATCH instead of POST. */
  edit?: boolean;
}

/** One per-currency AR position from GET /parties/:id/statement. */
interface PartyBalanceRow {
  currencyCode: string;
  invoiced: string;
  paid: string;
  balance: string;
}

interface LineRow {
  /** Stable client-side row key for React reconciliation. Without it (when the
   *  list keys on array index) deleting a middle row makes React reuse the
   *  Combobox/Input DOM by position, showing the deleted row's stale values. */
  _key: string;
  itemId?: string;
  itemUnitId?: string;
  unitName?: string;
  unitFactor?: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
}

let lineKeySeq = 0;
const nextLineKey = (): string => `ln-${++lineKeySeq}`;

const blankLine = (): LineRow => ({
  _key: nextLineKey(),
  description: '',
  quantity: '1',
  unitPrice: '0',
  taxRate: '0',
});

/** No customer group (or no group currency) ⇒ price in the tenant base currency. */
const NO_BASIS: PartyPricingBasis = {
  partyId: null,
  priceListId: null,
  priceListName: null,
  currencyCode: null,
  adjustmentType: 'none',
  adjustmentValue: null,
};

/** POST /pricing/resolve-bulk caps a request at 500 lines; longer documents are chunked. */
const BULK_PRICE_CHUNK = 500;

/** Minimal account shape for the "paid from" picker on a paid-now purchase. */
interface AccountOption {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  accountType: string;
  accountSubtype?: string | null;
}

/** A line's quantity as a positive number the pricing API accepts (for qty breaks). */
const lineQty = (quantity: string): number => {
  const q = Number(normalizeNumericInput(quantity));
  return Number.isFinite(q) && q > 0 ? Math.min(q, 1_000_000) : 1;
};

export function DocumentFormPage({
  endpoint, ns, partySide, hasDueDate, docDateLabelKey, dueDateLabelKey, listPath, edit = false,
}: DocumentFormPageProps): JSX.Element {
  // Routes pass `documents:salesOrders` (etc) to share one form across
  // every doctype. DocumentListPage parses that into ns + keyPrefix to
  // pick up the per-doctype `title/description/columns/empty` subtree.
  // The form keys (`form.*`, `actions.*`) intentionally live at the
  // namespace root because they're identical across doctypes — so we
  // strip the prefix and use only the namespace. Without this strip,
  // `useTranslation('documents:salesOrders')` treats the whole string
  // as a namespace name, no translations match, and every `t('form.x')`
  // call renders the literal key path.
  const nsName = ns.includes(':') ? (ns.split(':', 2)[0] ?? ns) : ns;
  const { t, i18n } = useTranslation(nsName);
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  const enabledModules = useEnabledModules(tenant?.id ?? null);
  /**
   * Lines the form CARRIES but does not edit — an online order's delivery
   * charge. The checkout books it as an item-less line so it lands in the total
   * like anything else, but in an editable grid it renders as a row with an
   * empty product box, which reads as a bug and invites someone to "fix" it by
   * deleting the fee. Held here, shown in the totals, and written back on save.
   */
  const [carriedLines, setCarriedLines] = useState<LineRow[]>([]);
  const [items, setItems] = useState<Item[] | null>(null);
  const [parties, setParties] = useState<Party[] | null>(null);
  // The pickers hold a PAGE of the catalogue (the API caps a list at 500, and
  // this shop has thousands of items). Everything below exists to make that page
  // stop being the ceiling: rows named by a saved document are fetched by id,
  // and typing widens the list from the server.
  const searchTimer = useRef<number | null>(null);
  const [hydrated, setHydrated] = useState(!edit);
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => new Date(Date.now() + 30 * 86400_000).toISOString().slice(0, 10));
  const [partyId, setPartyId] = useState('');
  const [currencyCode, setCurrencyCode] = useState(tenant?.baseCurrencyCode ?? 'USD');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineRow[]>([blankLine()]);
  // Documents that name a warehouse on the header: a bill/PO RECEIVES into it,
  // an invoice/sales order SHIPS from it (the SO carries it onto the invoice at
  // conversion). '' = the tenant default (bills) or the stocked-at inference
  // (invoices) — i.e. exactly what every pre-field document meant.
  const hasWarehouse = endpoint === '/bills' || endpoint === '/purchase-orders' || endpoint === '/invoices' || endpoint === '/sales-orders';
  const [warehouseId, setWarehouseId] = useState('');
  // Bills carry the SUPPLIER's own invoice number too — the shop's internal
  // BILL-# stays auto, this is what the supplier calls the paper.
  const isBill = endpoint === '/bills';
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // The selected customer's pricing basis: their group (price list) and the
  // currency that group's prices are denominated in — what they're shown AND
  // charged in. Resolved server-side; null when they have no group.
  const [basis, setBasis] = useState<PartyPricingBasis | null>(null);
  const [discountType, setDiscountType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [discountValue, setDiscountValue] = useState('0');
  const [couponCode, setCouponCode] = useState('');
  // null = no coupon applied; otherwise the previewed code + discount amount.
  const [couponApplied, setCouponApplied] = useState<{ code: string; amount: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** The register sale refusing this edit, when that is why the save failed. */
  const [blockingSale, setBlockingSale] = useState<BlockingPosSale | null>(null);
  const [voidingSale, setVoidingSale] = useState(false);
  // Voiding a register sale refunds its tender and puts the stock back, so it is
  // the same privilege as a refund at the till — never a plain invoice clerk's.
  const canVoidPosSale = usePermission('pos.refund');
  // Set when editing a document that has ALREADY posted to the ledger: holds the
  // journal entry that saving will reverse, so the form can warn before the fact.
  const [postedEntry, setPostedEntry] = useState<string | null>(null);
  // Selected customer's current AR balance (per currency). Loaded whenever a
  // customer is picked so the user sees what they already owe before invoicing.
  const [balance, setBalance] = useState<PartyBalanceRow[] | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // "Paid now": a cash purchase — post the bill and settle it in one go. Only
  // offered when creating a bill, and only to a user who could do all three
  // steps by hand (post the bill, create the payment, post the payment).
  const canPostBill = usePermission('bills.post');
  const canWritePayment = usePermission('payments.write');
  const canPostPayment = usePermission('payments.post');
  const supportsPayNow = endpoint === '/bills' && !edit && canPostBill && canWritePayment && canPostPayment;
  const [payNow, setPayNow] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [payAccountId, setPayAccountId] = useState('');
  /** Settle the whole bill, or hand over part of it now. */
  const [payFull, setPayFull] = useState(true);
  const [payAmount, setPayAmount] = useState('');
  const [cashAccounts, setCashAccounts] = useState<AccountOption[] | null>(null);
  // Order-level discount is a sales-side concept (customer documents only).
  const showDiscount = partySide === 'customer';
  const pricingOn = partySide === 'customer' && moduleEnabled(enabledModules, 'pricing');
  // Tax module: when off, hide the per-line tax INPUT (backend forces tax=0 anyway).
  const taxOn = moduleEnabled(enabledModules, 'tax');
  // A purchase bill can put what it brings in straight onto the online store:
  // a "Publish" box per product line — the same switch as /cms/products, ticked
  // when the product is already published, applied on save. Only for someone
  // who may publish, and only with the store module on.
  const canCmsWrite = usePermission('cms.write');
  const showPublish = isBill && canCmsWrite && moduleEnabled(enabledModules, 'cms');
  // itemId → what the store says now (and whether it can be on the store at all).
  const [publishKnown, setPublishKnown] = useState<Record<string, { sellable: boolean; published: boolean }>>({});
  // itemId → the box as ticked here.
  const [publishWant, setPublishWant] = useState<Record<string, boolean>>({});

  // Refs the async re-pricer reads: its closure would be a render behind, and a
  // sequence number lets a superseded answer (customer clicked twice) be dropped.
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const currencyRef = useRef(currencyCode);
  currencyRef.current = currencyCode;
  const repriceSeq = useRef(0);
  /** The customer the lines are already priced for — an edited draft keeps its saved prices. */
  const pricedPartyRef = useRef<string | null>(null);

  useEffect(() => {
    void Promise.all([
      api().get<{ items: Item[] }>('/items'),
      api().get<{ items: Party[] }>(`/parties?partyType=${partySide}`),
    ]).then(([i, p]) => {
      setItems(i.data.items);
      setParties(p.data.items);
      if (!edit) {
        // Honor a ?party= prefill (e.g. "new invoice for this customer" from a
        // statement); else default to the first active party.
        const wanted = searchParams.get('party');
        const target = (wanted && p.data.items.find((x) => x.id === wanted)) || p.data.items.find((x) => x.isActive);
        if (target) setPartyId(target.id);
      }
    });
  }, [partySide, edit, searchParams]);

  /** Add items we don't hold yet, keeping the ones already loaded. */
  const mergeItems = useCallback((incoming: Item[]): void => {
    setItems((prev) => {
      const have = new Set((prev ?? []).map((i) => i.id));
      const add = incoming.filter((i) => !have.has(i.id));
      return add.length === 0 ? prev : [...(prev ?? []), ...add];
    });
  }, []);

  const mergeParties = useCallback((incoming: Party[]): void => {
    setParties((prev) => {
      const have = new Set((prev ?? []).map((p) => p.id));
      const add = incoming.filter((p) => !have.has(p.id));
      return add.length === 0 ? prev : [...(prev ?? []), ...add];
    });
  }, []);

  /**
   * Search the server as the user types, and fold the answers into the loaded
   * page. Debounced, and only from two characters: one keystroke would fire a
   * query per letter for a result nobody can read yet.
   */
  const searchRemote = useCallback((kind: 'item' | 'party', raw: string): void => {
    const q = raw.trim();
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (q.length < 2) return;
    searchTimer.current = window.setTimeout(() => {
      if (kind === 'item') {
        void api().get<{ items: Item[] }>('/items', { params: { q, limit: 50 } })
          .then((r) => mergeItems(r.data.items))
          .catch(() => undefined);
      } else {
        void api().get<{ items: Party[] }>('/parties', { params: { q, partyType: partySide, limit: 50 } })
          .then((r) => mergeParties(r.data.items))
          .catch(() => undefined);
      }
    }, 250);
  }, [mergeItems, mergeParties, partySide]);

  // Drop a pending search when the form unmounts — it would resolve into a
  // setState on a gone component.
  useEffect(() => () => { if (searchTimer.current) window.clearTimeout(searchTimer.current); }, []);

  useEffect(() => {
    if (!hasWarehouse) return;
    void api().get<{ items: Warehouse[]; defaultWarehouseId?: string | null; allowedWarehouseIds?: string[] | null }>('/warehouses').then((r) => {
      const active = r.data.items
        .filter((w) => w.isActive)
        .sort((a, b) => a.code.localeCompare(b.code));
      setWarehouses(active);
      if (edit) return; // an edited document already made its choice (or meant the default)
      // A person confined to ONE branch writes that branch's documents, whatever
      // the shop-wide default says — the default may well be another branch,
      // which the server refuses them. The server files it there anyway; this
      // shows it.
      const allowed = r.data.allowedWarehouseIds ?? null;
      if (allowed && allowed.length === 1) {
        const mine = allowed[0]!;
        setWarehouseId((prev) => prev || mine);
        return;
      }
      // Pre-pick where the goods will actually move, so the form SHOWS the
      // consequence instead of leaving it to be discovered after posting:
      //  - bills/POs: the configured tenant default, else first-by-code — the
      //    server's own fallback either way.
      //  - invoices: only a CONFIGURED default. An unconfigured shop keeps the
      //    smarter stocked-at inference, which an arbitrary pre-pick would
      //    silently defeat.
      //  - sales orders: never — the warehouse there is optional intent.
      // The shop default only when it is one of this person's branches.
      const def = r.data.defaultWarehouseId && active.some((w) => w.id === r.data.defaultWarehouseId)
        ? r.data.defaultWarehouseId
        : null;
      if (endpoint === '/bills' || endpoint === '/purchase-orders') {
        const pick = def ?? active[0]?.id;
        if (pick) setWarehouseId((prev) => prev || pick);
      } else if (endpoint === '/invoices' && def) {
        setWarehouseId((prev) => prev || def);
      }
    });
  }, [hasWarehouse, edit, endpoint]);

  // Cash/bank accounts for the "paid from" picker — fetched only once the user
  // actually ticks "Paid now", so the normal credit-purchase form costs nothing.
  useEffect(() => {
    if (!payNow || cashAccounts) return;
    void api()
      .get<{ items: AccountOption[] }>('/accounts')
      .then((r) => {
        // Cash/bank ONLY. Offering every asset account let a user fund the
        // payment from e.g. Inventory (CR Inventory / DR AP), silently
        // corrupting the balance sheet. With none configured the list is empty
        // and the tenant's default cash account is used instead.
        setCashAccounts(r.data.items.filter(
          (a) => a.isActive && a.accountType === 'asset'
            && (a.accountSubtype === 'cash' || a.accountSubtype === 'bank'),
        ));
      })
      .catch(() => setCashAccounts([]));
  }, [payNow, cashAccounts]);

  // One de-duped warning toast (stable id) so re-pricing many lines at once
  // (e.g. on a customer switch) can't stack a wall of toasts.
  const warnPriceCurrency = useCallback((priceCurrency: string, docCurrency: string): void => {
    toast.warning(
      t('form.priceCurrencyMismatch', {
        currency: priceCurrency,
        docCurrency,
        defaultValue: 'This item is priced in {{currency}} — enter the price in {{docCurrency}}.',
      }),
      { id: 'doc-price-currency' },
    );
  }, [t]);

  /**
   * Re-price the whole document for a customer IN ONE COHERENT UPDATE: the server
   * returns their pricing basis (their group's own currency) AND every line's
   * price in the same answer, so the header currency can never run ahead of the
   * numbers under it. The old two-step did exactly that — it flipped the header
   * to the group currency while the lines kept the base-currency magnitudes, so
   * a 2,500 IQD item was billed as $2,500.
   *
   * Nothing is ever converted client-side: a price we can't express in the
   * document currency is CLEARED (and flagged) instead of copied at face value.
   * `forcedCurrency` = the user chose a currency by hand — we keep their choice
   * and re-price into it.
   */
  const repriceForParty = useCallback(async (pid: string, forcedCurrency?: string): Promise<void> => {
    if (!pricingOn || !pid) return;
    const seq = ++repriceSeq.current;
    let forced = forcedCurrency;
    // At most two passes: a line added while the request was in flight was priced
    // in the pre-change currency, so it gets cleared below and re-priced by pass 2.
    for (let pass = 0; pass < 2; pass += 1) {
      const rows = linesRef.current.filter((l) => l.itemId);
      const prevCurrency = currencyRef.current;
      let nextBasis: PartyPricingBasis;
      let priced: BulkPriceLine[] = [];
      try {
        if (rows.length === 0) {
          nextBasis = await getPartyPricingBasis({ partyId: pid });
        } else {
          const chunks: LineRow[][] = [];
          for (let i = 0; i < rows.length; i += BULK_PRICE_CHUNK) chunks.push(rows.slice(i, i + BULK_PRICE_CHUNK));
          const answers = await Promise.all(chunks.map((chunk) => resolvePricesBulk({
            partyId: pid,
            // Only a tie-breaker preference; the answer's own currency is authoritative.
            ...(forced ? { currency: forced } : {}),
            lines: chunk.map((l) => ({
              itemId: l.itemId as string,
              itemUnitId: l.itemUnitId ?? null,
              qty: lineQty(l.quantity),
            })),
          })));
          nextBasis = answers[0]?.basis ?? NO_BASIS;
          priced = answers.flatMap((a) => a.items);
        }
      } catch {
        return; // pricing unavailable (offline / no permission) — leave the form untouched
      }
      if (seq !== repriceSeq.current) return; // a newer customer/currency won
      // The group's own currency is what this customer is shown and charged in;
      // no group (or no group currency) ⇒ the tenant base currency.
      const target = forced ?? nextBasis.currencyCode ?? baseCurrency;
      // A hand-picked currency counts as changed even though the header already
      // shows it (the picker sets it before asking us) — its lines still hold the
      // PREVIOUS currency's magnitudes and must all be re-priced or cleared.
      const currencyChanged = target !== prevCurrency || (pass === 0 && forcedCurrency != null);
      const byKey = new Map<string, BulkPriceLine>();
      rows.forEach((l, i) => { const p = priced[i]; if (p) byKey.set(l._key, p); });
      setBasis(nextBasis);
      setCurrencyCode(target);
      currencyRef.current = target;
      setLines((prev) => prev.map((l) => {
        if (!l.itemId) return l;
        const p = byKey.get(l._key);
        // A price with no currency of its own is denominated in the tenant base
        // currency — treating it as "matches anything" is what let an IQD number
        // land on a USD document.
        if (p?.unitPrice != null && (p.currencyCode ?? baseCurrency) === target) {
          return { ...l, unitPrice: trimMoney(p.unitPrice) };
        }
        // Nothing usable in the document currency: never leave behind a number
        // that means something else (2,500 IQD rendering as $2,500.00).
        const foreign = p?.currencyCode != null && p.currencyCode !== target;
        return currencyChanged || foreign ? { ...l, unitPrice: '' } : l;
      }));
      const mismatch = priced.find((p) => p.currencyCode != null && p.currencyCode !== target)?.currencyCode;
      if (mismatch) warnPriceCurrency(mismatch, target);
      // Let React commit before looking for lines added while we were waiting.
      await new Promise<void>((resolve) => { window.setTimeout(resolve, 0); });
      if (seq !== repriceSeq.current) return;
      if (!linesRef.current.some((l) => l.itemId && !byKey.has(l._key))) return;
      forced = target; // pass 2 keeps the currency we just settled on
    }
  }, [baseCurrency, pricingOn, warnPriceCurrency]);

  // The customer drives the pricing: whenever the selection changes, re-price the
  // document into their group's currency (header + lines together). A hydrated
  // draft is recorded as already priced, so editing one keeps what was saved.
  useEffect(() => {
    if (!hydrated || !pricingOn) return undefined;
    if (!partyId) {
      // Clearing the customer also cancels a re-price still in flight for the
      // previous one — its answer must not land on a party-less document.
      repriceSeq.current += 1;
      pricedPartyRef.current = null;
      setBasis(null);
      return undefined;
    }
    if (pricedPartyRef.current === partyId) {
      // Already priced for this customer — just show which group they're on.
      let cancelled = false;
      void getPartyPricingBasis({ partyId })
        .then((b) => { if (!cancelled) setBasis(b); })
        .catch(() => undefined);
      return () => { cancelled = true; };
    }
    pricedPartyRef.current = partyId;
    void repriceForParty(partyId);
    return undefined;
  }, [partyId, pricingOn, hydrated, repriceForParty]);

  // Fetch the customer's current account balance whenever the selection
  // changes. AR-only (customers); vendors have no statement here. Failures
  // (e.g. missing parties.read) are non-fatal — the panel just stays hidden.
  useEffect(() => {
    if (partySide !== 'customer' || !partyId) {
      setBalance(null);
      return undefined;
    }
    let cancelled = false;
    setBalanceLoading(true);
    void api()
      .get<{ summary: PartyBalanceRow[] }>(`/parties/${partyId}/statement`)
      .then((r) => { if (!cancelled) setBalance(r.data.summary); })
      .catch(() => { if (!cancelled) setBalance(null); })
      .finally(() => { if (!cancelled) setBalanceLoading(false); });
    return () => { cancelled = true; };
  }, [partyId, partySide]);

  useEffect(() => {
    if (!edit || !id) return;
    void api()
      .get<{
        status: string;
        docDate: string;
        dueDate?: string | null;
        partyId: string;
        currencyCode: string;
        notes?: string | null;
        discountType?: 'none' | 'percent' | 'fixed' | null;
        discountValue?: string | null;
        discountTotal?: string | null;
        couponCode?: string | null;
        warehouseId?: string | null;
        postedJournalEntryNumber?: string | null;
        postedJournalEntryReversed?: boolean;
        isCreditNote?: boolean | null;
        isDebitNote?: boolean | null;
        lines?: Array<{ itemId?: string | null; itemUnitId?: string | null; unitName?: string | null; unitFactor?: string | null; description?: string | null; quantity: string; unitPrice: string; taxRate: string }>;
      }>(`${endpoint}/${id}`)
      .then((r) => {
        // Invoices and bills stay editable after posting — saving reverses their
        // journal entry (and a bill's stock receipt) and re-posts. Every other
        // doctype, a cancelled document, and credit/debit notes remain read-only.
        const revisable =
          (endpoint === '/invoices' || endpoint === '/bills') &&
          !r.data.isCreditNote && !r.data.isDebitNote &&
          r.data.status !== 'cancelled' && !r.data.postedJournalEntryReversed;
        // A SUBMITTED sales order is still open: nothing posted, no stock moved,
        // so the server rewrites it in place. This is how an online order (which
        // is born submitted) gets corrected instead of cancelled and re-typed.
        const openOrder = endpoint === '/sales-orders' && r.data.status === 'submitted';
        if (r.data.status !== 'draft' && !openOrder) {
          // No journal entry means there is nothing to reverse — fall back to
          // read-only rather than promising to reverse entry "".
          if (!revisable || !r.data.postedJournalEntryNumber) {
            setError(t('form.errors.notDraft', { defaultValue: 'Only drafts can be edited.' }));
            return;
          }
          setPostedEntry(r.data.postedJournalEntryNumber);
        }
        setDocDate(r.data.docDate.slice(0, 10));
        if (r.data.dueDate) setDueDate(r.data.dueDate.slice(0, 10));
        setPartyId(r.data.partyId);
        // The draft is already priced for this customer in this currency — mark it
        // so selecting the SAME party doesn't re-price a document the user saved.
        pricedPartyRef.current = r.data.partyId;
        setCurrencyCode(r.data.currencyCode);
        currencyRef.current = r.data.currencyCode;
        setNotes(r.data.notes ?? '');
        setSupplierInvoiceNo((r.data as { supplierInvoiceNo?: string | null }).supplierInvoiceNo ?? '');
        if (r.data.warehouseId) setWarehouseId(r.data.warehouseId);
        setDiscountType(r.data.discountType ?? 'none');
        setDiscountValue(trimMoney(r.data.discountValue ?? '0'));
        if (r.data.couponCode) {
          setCouponCode(r.data.couponCode);
          setCouponApplied({ code: r.data.couponCode, amount: r.data.discountTotal ?? '0' });
        }
        // An ONLINE order's item-less line is its delivery charge, by
        // construction (StorefrontCheckoutService adds exactly one). Everywhere
        // else an item-less line is a deliberate free-text line and stays
        // editable, so this split is scoped to that one case.
        const isOnlineOrder = endpoint === '/sales-orders' && (r.data as { channel?: string | null }).channel === 'online';
        const editableLines = isOnlineOrder ? (r.data.lines ?? []).filter((l) => l.itemId) : (r.data.lines ?? []);
        if (isOnlineOrder) {
          setCarriedLines((r.data.lines ?? []).filter((l) => !l.itemId).map((l) => ({
            _key: nextLineKey(),
            description: l.description ?? '',
            quantity: trimMoney(l.quantity),
            unitPrice: trimMoney(l.unitPrice),
            taxRate: trimMoney(l.taxRate),
          })));
        }
        if (editableLines.length > 0) {
          setLines(editableLines.map((l) => ({
            _key: nextLineKey(),
            itemId: l.itemId ?? undefined,
            itemUnitId: l.itemUnitId ?? undefined,
            unitName: l.unitName ?? undefined,
            unitFactor: l.unitFactor ?? undefined,
            description: l.description ?? '',
            quantity: trimMoney(l.quantity),
            unitPrice: trimMoney(l.unitPrice),
            taxRate: trimMoney(l.taxRate),
          })));
          // Fetch the LINES' OWN items by id. The picker only ever loaded the
          // first page of the catalogue, so a line pointing anywhere past it
          // rendered with an empty product box (and lost its unit list and its
          // re-pricing with it) — the saved document looked like it had
          // forgotten what it was for.
          const lineItemIds = [...new Set(editableLines.map((l) => l.itemId).filter((v): v is string => !!v))];
          if (lineItemIds.length > 0) {
            void api()
              .get<{ items: Item[] }>('/items', { params: { ids: lineItemIds.join(',') } })
              .then((res) => mergeItems(res.data.items))
              .catch(() => undefined);
          }
        }
        // …and the document's own party, for the same reason.
        if (r.data.partyId) {
          void api()
            .get<Party>(`/parties/${r.data.partyId}`)
            .then((res) => mergeParties([res.data]))
            .catch(() => undefined);
        }
        setHydrated(true);
      });
  }, [edit, id, endpoint, t, mergeItems, mergeParties]);

  const totals = useMemo(() => {
    let subtotal = new Decimal(0);
    let tax = new Decimal(0);
    for (const line of lines) {
      const qty = safeDecimal(line.quantity);
      const price = safeDecimal(line.unitPrice);
      const rate = safeDecimal(line.taxRate);
      const lineSubtotal = qty.times(price);
      const taxAmount = lineSubtotal.times(rate);
      subtotal = subtotal.plus(lineSubtotal);
      tax = tax.plus(taxAmount);
    }
    const dv = safeDecimal(discountValue);
    let discount = new Decimal(0);
    if (showDiscount && couponApplied) {
      // The server recomputes authoritatively on save; this previews it.
      discount = Decimal.min(safeDecimal(couponApplied.amount), subtotal);
    } else if (showDiscount && discountType === 'percent') {
      discount = Decimal.min(subtotal.times(dv).div(100), subtotal);
    } else if (showDiscount && discountType === 'fixed') {
      discount = Decimal.min(dv, subtotal);
    }
    discount = Decimal.max(discount, new Decimal(0));
    // The carried delivery charge is not editable here but it IS part of the
    // order: leaving it out would show a total the saved order doesn't have.
    let carried = new Decimal(0);
    for (const line of carriedLines) carried = carried.plus(safeDecimal(line.quantity).times(safeDecimal(line.unitPrice)));
    return {
      subtotal: subtotal.toFixed(4),
      tax: tax.toFixed(4),
      discount: discount.toFixed(4),
      carried: carried.toFixed(4),
      total: subtotal.minus(discount).plus(tax).plus(carried).toFixed(4),
    };
  }, [lines, carriedLines, discountType, discountValue, showDiscount, couponApplied]);

  // Keep an applied coupon's previewed discount in sync with the live cart: the
  // server recomputes on save, so re-validate (debounced) whenever the lines,
  // currency, or customer change — and drop the coupon if it no longer applies.
  const appliedCode = couponApplied?.code;
  useEffect(() => {
    if (!showDiscount || !appliedCode) return undefined;
    const handle = window.setTimeout(() => {
      const payloadLines = lines.map((l) => ({
        itemId: l.itemId,
        lineSubtotal: safeDecimal(l.quantity).times(safeDecimal(l.unitPrice)).toFixed(4),
      }));
      void api()
        .post<{ code: string; discountAmount: string }>('/coupons/validate', {
          code: appliedCode,
          currencyCode,
          partyId: partyId || undefined,
          lines: payloadLines,
        })
        .then((r) => setCouponApplied({ code: r.data.code, amount: r.data.discountAmount }))
        .catch(() => {
          setCouponApplied(null);
          setCouponCode('');
          setError(t('form.coupon.noLongerApplies', { defaultValue: 'The coupon no longer applies to this document.' }));
        });
    }, 400);
    return () => window.clearTimeout(handle);
    // appliedCode (not the whole object) so updating the amount doesn't re-loop.
  }, [lines, currencyCode, partyId, showDiscount, appliedCode, t]);

  // Searchable picker options. Items carry their barcode in `keywords` (and as
  // a dim sublabel) so typing or scanning a barcode finds the item.
  // A document that was saved with a party/item which has since been
  // DEACTIVATED still has to render it — dropping it here would blank the
  // selection just as surely as never loading it. Active rows, plus whatever
  // this document already points at.
  const partyOptions = useMemo<ComboboxOption[]>(
    () =>
      (parties ?? [])
        .filter((p) => p.isActive || p.id === partyId)
        .map((p) => ({ value: p.id, label: `${p.code} — ${p.displayName}`, keywords: `${p.code} ${p.displayName}` })),
    [parties, partyId],
  );
  const pickedItemIds = useMemo(
    () => new Set(lines.map((l) => l.itemId).filter((v): v is string => !!v)),
    [lines],
  );
  const itemOptions = useMemo<ComboboxOption[]>(
    () =>
      (items ?? [])
        .filter((it) => it.isActive || pickedItemIds.has(it.id))
        .map((it) => ({
          value: it.id,
          label: `${it.code} — ${it.name}`,
          sublabel: it.barcode ?? undefined,
          keywords: `${it.code} ${it.name} ${it.barcode ?? ''}`,
          exact: [it.barcode ?? '', ...(it.barcodes ?? [])].filter(Boolean),
        })),
    [items, pickedItemIds],
  );
  // Scan-to-add options: the base items PLUS one entry per packaging-unit barcode.
  // Unit entries match ONLY on their barcode (not the item name), so scanning a
  // pack/carton barcode adds the line with that unit pre-selected, while typing a
  // name keeps the list to one row per item.
  const scanOptions = useMemo<ComboboxOption[]>(() => {
    const opts: ComboboxOption[] = [];
    for (const it of items ?? []) {
      if (!it.isActive) continue;
      opts.push({
        value: it.id,
        label: `${it.code} — ${it.name}`,
        sublabel: it.barcode ?? undefined,
        keywords: `${it.code} ${it.name} ${it.barcode ?? ''}`,
        // A scanned label identifies this row outright — it must not lose to a
        // product whose CODE happens to contain the same digits.
        exact: [it.barcode ?? '', ...(it.barcodes ?? [])].filter(Boolean),
      });
      for (const u of it.units ?? []) {
        if (u.isActive && u.barcode) {
          opts.push({
            value: `${it.id}::${u.id}`,
            label: `${it.code} — ${it.name} · ${u.name} (×${trimMoney(u.factor)})`,
            sublabel: u.barcode,
            keywords: u.barcode, // barcode-only ⇒ surfaces on a scan, not on a name search
            exact: [u.barcode],
          });
        }
      }
    }
    return opts;
  }, [items]);

  // The products on this bill whose store state is not known yet — asked for
  // only those, never the whole catalogue.
  const publishItemKey = showPublish
    ? [...new Set(lines.map((l) => l.itemId).filter((v): v is string => !!v))].sort().join(',')
    : '';
  useEffect(() => {
    if (!publishItemKey) return;
    const missing = publishItemKey.split(',').filter((iid) => !(iid in publishKnown));
    if (missing.length === 0) return;
    let on = true;
    void api()
      .get<{ items: Array<{ itemId: string; sellable: boolean; isPublished: boolean }> }>('/cms/products/publish-state', {
        params: { ids: missing.join(',') },
      })
      .then((r) => {
        if (!on) return;
        setPublishKnown((m) => ({
          ...m,
          ...Object.fromEntries(r.data.items.map((x) => [x.itemId, { sellable: x.sellable, published: x.isPublished }])),
        }));
        // Ticked when already published — the box shows the store as it is.
        setPublishWant((m) => ({
          ...Object.fromEntries(r.data.items.map((x) => [x.itemId, x.isPublished])),
          ...m,
        }));
      })
      .catch(() => { /* no column state — the boxes stay hidden for these lines */ });
    return () => { on = false; };
    // publishKnown is read to skip what is known; listing it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishItemKey]);

  /**
   * Purchase documents: what each product was last BOUGHT at and what it SELLS at.
   *
   * The shop's old system showed «آخر سعر» beside the price being typed, so whoever
   * keys a supplier's bill saw at once that a pen went from $1.20 to $1.35 — and
   * then fixed the selling price. Here that took opening every product's page
   * (owner's video, 2026-10-28). One call for the products on the document; read
   * again when the window regains focus, i.e. after a price was changed in the tab
   * the hint opens. Cost data: a viewer the API refuses simply gets no hints.
   */
  const isPurchaseDoc = endpoint === '/bills' || endpoint === '/purchase-orders';
  const [buyCtx, setBuyCtx] = useState<Record<string, ItemPurchaseContext>>({});
  const buyItemKey = isPurchaseDoc
    ? [...new Set(lines.map((l) => l.itemId).filter((v): v is string => !!v))].sort().join(',')
    : '';
  const buyItemKeyRef = useRef(buyItemKey);
  buyItemKeyRef.current = buyItemKey;
  const loadBuyCtx = useCallback((ids: string[]): void => {
    if (ids.length === 0) return;
    void api()
      .get<{ items: ItemPurchaseContext[] }>('/items/purchase-context', {
        // A bill being edited is not its own "last purchase".
        params: { ids: ids.join(','), ...(edit && id && endpoint === '/bills' ? { excludeBill: id } : {}) },
      })
      .then((r) => setBuyCtx((m) => ({ ...m, ...Object.fromEntries(r.data.items.map((x) => [x.itemId, x])) })))
      .catch(() => { /* no cost permission or offline — the lines just show no hints */ });
  }, [edit, id, endpoint]);
  useEffect(() => {
    if (!buyItemKey) return;
    loadBuyCtx(buyItemKey.split(',').filter((iid) => !(iid in buyCtx)));
    // buyCtx is read to skip what is known; listing it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyItemKey, loadBuyCtx]);
  useEffect(() => {
    if (!isPurchaseDoc) return undefined;
    const onFocus = (): void => { if (buyItemKeyRef.current) loadBuyCtx(buyItemKeyRef.current.split(',')); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isPurchaseDoc, loadBuyCtx]);

  if (!items || !parties || !hydrated) return <Card><Skeleton className="h-32 w-full" /></Card>;

  // Chokepoint: every numeric line field is normalized to ASCII here (Arabic /
  // Persian digits → 0-9) so the value shown, stored, and later fed to Decimal
  // — and POSTed to the API — is always parseable. Idempotent for values set
  // programmatically (item pick / price list), which are already ASCII.
  const cleanPatch = (patch: Partial<LineRow>): Partial<LineRow> => {
    const clean: Partial<LineRow> = { ...patch };
    if (typeof clean.quantity === 'string') clean.quantity = normalizeNumericInput(clean.quantity);
    if (typeof clean.unitPrice === 'string') clean.unitPrice = normalizeNumericInput(clean.unitPrice);
    if (typeof clean.taxRate === 'string') clean.taxRate = normalizeNumericInput(clean.taxRate);
    return clean;
  };
  const updateLine = (idx: number, patch: Partial<LineRow>): void => {
    const clean = cleanPatch(patch);
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...clean } : l)));
  };
  /** Patch a line by its stable key — for async results, where an index may have moved. */
  const updateLineByKey = (key: string, patch: Partial<LineRow>): void => {
    const clean = cleanPatch(patch);
    setLines((prev) => prev.map((l) => (l._key === key ? { ...l, ...clean } : l)));
  };
  const addLine = (): void => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (idx: number): void => setLines((prev) => prev.filter((_, i) => i !== idx));

  // The currency an item's stored price is denominated in — its sale/purchase
  // currency, or the tenant base when unset (mirrors the POS backend guard).
  // Packaging-unit prices inherit the item's currency.
  const itemPriceCurrency = (item: Item): string =>
    ((partySide === 'customer' ? item.saleCurrencyCode : item.purchaseCurrencyCode) ?? baseCurrency);

  // Seed a line's unit price from an item (+ optional packaging unit). When the
  // stored price is in a currency other than the document's, copying the raw
  // number would be ~FX-wrong (e.g. a $10 item onto an IQD invoice), so blank the
  // price and flag a mismatch instead of trusting a value the server would take
  // at face value. The customer's GROUP price (resolved right after, in the
  // group's own currency) is what normally fills the line in.
  const seedUnitPrice = (
    item: Item, unit?: ItemUnit, currency: string = currencyCode,
  ): { unitPrice: string; mismatch: boolean } => {
    const base = partySide === 'customer' ? item.salePrice : item.purchasePrice;
    const raw = unit ? ((partySide === 'customer' ? unit.salePrice : unit.purchasePrice) ?? base) : base;
    if (raw == null) return { unitPrice: '0', mismatch: false };
    if (itemPriceCurrency(item) !== currency) return { unitPrice: '', mismatch: true };
    return { unitPrice: trimMoney(raw), mismatch: false };
  };

  /**
   * Put the customer's group price (incl. quantity breaks) on one line. A group's
   * prices are denominated in the GROUP's own currency — which is exactly what the
   * header currency was set to — so this is what makes a foreign-currency customer
   * type work at all. A price in any OTHER currency is cleared, never converted.
   *
   * `fallbackCurrency` = the item's own price currency when the seed had to be
   * blanked; warned only if no group price rescues the line (the old code warned
   * and returned BEFORE asking, so group prices were never used).
   */
  const applyResolvedPrice = async (
    key: string,
    itemId: string | undefined,
    qty: string,
    pid = partyId,
    itemUnitId?: string,
    fallbackCurrency?: string,
  ): Promise<void> => {
    const docCurrency = currencyCode;
    if (pricingOn && pid && itemId) {
      try {
        const r = await resolvePrice({ itemId, itemUnitId, partyId: pid, qty: lineQty(qty), currency: docCurrency });
        // The document currency moved while we were asking (a customer switch
        // re-prices everything) — that pass owns this line now.
        if (docCurrency !== currencyRef.current) return;
        if (r.unitPrice != null && (r.source === 'list-item' || r.source === 'list-percent')) {
          // A price with no currency of its own is in the tenant base currency.
          if ((r.currencyCode ?? baseCurrency) === docCurrency) {
            updateLineByKey(key, { unitPrice: trimMoney(r.unitPrice) });
            return;
          }
          updateLineByKey(key, { unitPrice: '' });
          warnPriceCurrency(r.currencyCode ?? baseCurrency, docCurrency);
          return;
        }
      } catch { /* keep the seeded price */ }
    }
    if (fallbackCurrency && docCurrency === currencyRef.current) warnPriceCurrency(fallbackCurrency, docCurrency);
  };

  const onItemPicked = (idx: number, itemId: string): void => {
    const item = items.find((it) => it.id === itemId);
    if (!item) {
      updateLine(idx, { itemId: undefined, itemUnitId: undefined, unitName: undefined, unitFactor: undefined });
      return;
    }
    const line = lines[idx];
    // Picking a (new) item resets the line to its base unit.
    const seed = seedUnitPrice(item);
    updateLine(idx, {
      itemId: item.id,
      itemUnitId: undefined,
      unitName: undefined,
      unitFactor: undefined,
      description: item.name,
      unitPrice: seed.unitPrice,
    });
    if (line) {
      void applyResolvedPrice(
        line._key, item.id, line.quantity, partyId, undefined,
        seed.mismatch ? itemPriceCurrency(item) : undefined,
      );
    }
  };

  /**
   * Pick a packaging unit (or base) for a line. Sets unitPrice to the unit's
   * own price (sale/purchase per side) falling back to the item base, and
   * snapshots name + factor for printing.
   */
  const onUnitPicked = (idx: number, itemId: string | undefined, unitId: string): void => {
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    const line = lines[idx];
    if (!unitId) {
      const seed = seedUnitPrice(item);
      updateLine(idx, {
        itemUnitId: undefined,
        unitName: undefined,
        unitFactor: undefined,
        unitPrice: seed.unitPrice,
      });
      // Restore the per-base group price for the customer.
      if (line) {
        void applyResolvedPrice(
          line._key, item.id, line.quantity, partyId, undefined,
          seed.mismatch ? itemPriceCurrency(item) : undefined,
        );
      }
      return;
    }
    const u = item.units.find((x) => x.id === unitId);
    if (!u) return;
    const seed = seedUnitPrice(item, u);
    updateLine(idx, {
      itemUnitId: u.id,
      unitName: u.name,
      unitFactor: u.factor,
      unitPrice: seed.unitPrice,
    });
    // Apply the customer's per-unit group override for this packaging unit.
    if (line) {
      void applyResolvedPrice(
        line._key, item.id, line.quantity, partyId, u.id,
        seed.mismatch ? itemPriceCurrency(item) : undefined,
      );
    }
  };

  /**
   * Add a new line for an item picked/scanned from the search bar. Replaces the
   * initial blank line on the first add, then appends — so scanning fills the
   * document top-down.
   */
  // Append a line for an item (+ optional packaging unit) and price it for the
  // customer: seed from the item's own price (sale/purchase per side, unit price
  // when a packaging unit was scanned), then let the group price take over — in
  // the group's currency, which is the document currency.
  const addPricedLine = (item: Item, unit?: ItemUnit): void => {
    const seed = seedUnitPrice(item, unit);
    const row: LineRow = {
      _key: nextLineKey(),
      itemId: item.id,
      ...(unit ? { itemUnitId: unit.id, unitName: unit.name, unitFactor: unit.factor } : {}),
      description: item.name,
      quantity: '1',
      unitPrice: seed.unitPrice,
      taxRate: '0',
    };
    appendLine(row);
    void applyResolvedPrice(
      row._key, item.id, row.quantity, partyId, unit?.id,
      seed.mismatch ? itemPriceCurrency(item) : undefined,
    );
  };
  // Replace the initial blank line on the first add, then append — so scanning
  // fills the document top-down.
  const appendLine = (row: LineRow): void => {
    setLines((prev) => {
      const last = prev[prev.length - 1];
      const lastBlank =
        prev.length === 1 && !!last && !last.itemId && last.description === '' && last.unitPrice === '0';
      return lastBlank ? [row] : [...prev, row];
    });
  };

  const addItemLine = (value?: string): void => {
    if (!value) return;
    // A scanned unit barcode arrives as "itemId::unitId" (see scanOptions); a plain
    // id is the item's base unit. A unit barcode pre-selects that unit + its price.
    const [itemId, unitId] = value.split('::');
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    const unit = unitId ? item.units.find((u) => u.id === unitId) : undefined;
    addPricedLine(item, unit);
  };

  // Fallback for a scanned barcode that matched no loaded option (e.g. an item
  // beyond the first page of the catalogue). Resolves server-side with the same
  // precedence as the POS scanner (item code → item barcode → unit barcode) and
  // adds the line — pre-selecting the packaging unit when a unit barcode matched.
  const resolveBarcode = async (code: string): Promise<void> => {
    try {
      const { data } = await api().get<{ item: Item; itemUnitId: string | null }>('/items/resolve', { params: { barcode: code } });
      const item = data.item;
      // Merge the resolved item into the loaded set so its line renders the unit
      // dropdown / name / price like any picked item.
      setItems((prev) => (!prev ? [item] : prev.some((it) => it.id === item.id) ? prev : [...prev, item]));
      const unit = data.itemUnitId ? item.units.find((u) => u.id === data.itemUnitId) : undefined;
      addPricedLine(item, unit);
    } catch {
      toast.error(t('form.unknownBarcode', { defaultValue: 'No item found for that barcode.' }));
    }
  };

  /**
   * A manual currency override stays possible — but it must never leave the old
   * currency's magnitudes on screen. With group pricing we re-price the whole
   * document from the server in the chosen currency; otherwise (vendor documents,
   * or the pricing module off) we re-seed from the items' own prices and clear
   * whatever can't be expressed there. Nothing is converted client-side.
   */
  const onCurrencyChange = (next: string): void => {
    if (next === currencyCode) return;
    setCurrencyCode(next);
    currencyRef.current = next;
    if (pricingOn && partyId) { void repriceForParty(partyId, next); return; }
    let foreign: string | null = null;
    const seeded = new Map<string, string>();
    for (const l of lines) {
      const item = l.itemId ? items.find((it) => it.id === l.itemId) : undefined;
      if (!item) continue;
      const unit = l.itemUnitId ? item.units.find((u) => u.id === l.itemUnitId) : undefined;
      const seed = seedUnitPrice(item, unit, next);
      seeded.set(l._key, seed.unitPrice);
      if (seed.mismatch) foreign = itemPriceCurrency(item);
    }
    setLines((prev) => prev.map((l) => {
      const price = seeded.get(l._key);
      return price === undefined ? l : { ...l, unitPrice: price };
    }));
    if (foreign) warnPriceCurrency(foreign, next);
  };

  // Preview a coupon against the current lines (the server recomputes on save).
  const applyCoupon = async (): Promise<void> => {
    const code = couponCode.trim();
    if (!code) return;
    setError(null);
    try {
      const payloadLines = lines.map((l) => ({
        itemId: l.itemId,
        lineSubtotal: safeDecimal(l.quantity).times(safeDecimal(l.unitPrice)).toFixed(4),
      }));
      const r = await api().post<{ code: string; discountAmount: string }>('/coupons/validate', {
        code,
        currencyCode,
        partyId: partyId || undefined,
        lines: payloadLines,
      });
      setCouponApplied({ code: r.data.code, amount: r.data.discountAmount });
      setDiscountType('none');
      setDiscountValue('0');
    } catch (err) {
      const c = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(
        c === 'not_found'
          ? t('form.coupon.notFound', { defaultValue: 'No such coupon code.' })
          : c === 'unprocessable'
            ? t('form.coupon.invalid', { defaultValue: "Coupon can't be applied to this document." })
            : tc('errors:server'),
      );
    }
  };

  const removeCoupon = (): void => {
    setCouponApplied(null);
    setCouponCode('');
    // The header stored the coupon's type/value as a snapshot; clear it so it
    // doesn't reappear as a manual discount the user never entered.
    setDiscountType('none');
    setDiscountValue('0');
  };

  const publishable = publishItemKey
    ? publishItemKey.split(',').filter((iid) => publishKnown[iid]?.sellable)
    : [];
  const publishAll = publishable.length > 0 && publishable.every((iid) => publishWant[iid]);
  const publishSome = publishable.some((iid) => publishWant[iid]);

  /** After the bill saved: put the store in line with the boxes (only what changed). */
  const applyPublish = async (): Promise<void> => {
    if (!showPublish) return;
    const changes = publishable
      .filter((iid) => (publishWant[iid] ?? false) !== (publishKnown[iid]?.published ?? false))
      .map((iid) => ({ itemId: iid, isPublished: publishWant[iid] ?? false }));
    if (changes.length === 0) return;
    try {
      await api().post('/cms/products/publish-state', { items: changes });
      const on = changes.filter((c) => c.isPublished).length;
      const off = changes.length - on;
      toast.success(t('form.publish.saved', {
        defaultValue: 'Store updated: {{on}} published, {{off}} unpublished',
        on: String(on),
        off: String(off),
      }));
    } catch {
      // The bill itself is saved; only the store step failed — say so, plainly.
      toast.error(t('form.publish.failed', { defaultValue: 'The bill is saved, but the store could not be updated. Publish the products from the store page.' }));
    }
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (saving) return; // guard against a rapid double-submit creating two documents
    if (!partyId) {
      setError(t('form.errors.noParty'));
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        docDate,
        partyId,
        currencyCode,
        notes: notes || undefined,
        // …plus the charge lines this form carried but never showed, unchanged
        // and last, so an edit can't silently drop the delivery fee.
        lines: [...lines, ...carriedLines].map((l) => ({
          itemId: l.itemId,
          itemUnitId: l.itemUnitId || undefined,
          unitName: l.unitName || undefined,
          unitFactor: l.unitFactor || undefined,
          description: l.description || undefined,
          quantity: l.quantity || '0',
          unitPrice: l.unitPrice || '0',
          taxRate: l.taxRate || '0',
        })),
      };
      if (hasDueDate) body.dueDate = dueDate;
      if (hasWarehouse && warehouseId) body.warehouseId = warehouseId;
      if (isBill) body.supplierInvoiceNo = supplierInvoiceNo.trim() || undefined;
      if (showDiscount) {
        if (couponApplied) {
          // Coupon drives the discount; the server recomputes + validates it.
          body.couponCode = couponApplied.code;
        } else {
          body.discountType = discountType;
          body.discountValue = discountValue || '0';
        }
      }
      if (edit && id) {
        await api().patch(`${endpoint}/${id}`, body);
        await applyPublish();
        if (postedEntry !== null) {
          toast.success(t('revise.reposted', {
            entry: postedEntry,
            defaultValue: 'Saved — journal entry {{entry}} was reversed and the document re-posted.',
          }));
        }
        navigate(`${listPath}/${id}`);
      } else {
        const created = await api().post<{ id: string }>(endpoint, body);
        await applyPublish();
        if (payNow && supportsPayNow && created.data?.id) {
          await settleNow(created.data.id);
          return;
        }
        navigate(listPath);
      }
    } catch (err) {
      // reviseErrorMessage names the specific reason a POSTED document couldn't
      // be re-posted (closed period, allocated payment, stock already sold, …)
      // and falls back to the generic rate/unprocessable/server messages.
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(
        code === 'unprocessable' && !(err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code
          ? t('form.errors.unprocessable')
          : reviseErrorMessage(err, t, tc),
      );
      // ...and when a register sale is what is standing in the way, hold on to
      // it so the panel below can offer the void instead of leaving the reader
      // to go and find POS-000255 by hand.
      setBlockingSale(blockingPosSale(err));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Void the register sale that is refusing this edit, from here.
   *
   * Deliberately the SAME endpoint the till's own void uses — it refunds the
   * tender, puts the stock back and reverses the sale's postings. Nothing here
   * bypasses the refusal; it performs the undo the refusal is asking for, so
   * the register and the invoice can never end up disagreeing about a sale.
   *
   * Confirmed first, and the reason is recorded: this gives money back.
   */
  const voidBlockingSale = async (): Promise<void> => {
    if (!blockingSale) return;
    const ok = await confirmDialog({
      title: t('revise.posBlock.confirmTitle', { defaultValue: 'Void this register sale?' }),
      message: t('revise.posBlock.confirmBody', {
        sale: blockingSale.saleNumber,
        defaultValue: 'Sale {{sale}} will be voided: whatever was tendered is refunded, the stock goes back, and the invoice it raised is cancelled with it. This cannot be undone — the sale has to be rung again.',
      }),
      tone: 'danger',
      confirmLabel: t('revise.posBlock.void', { defaultValue: 'Void the register sale' }),
    });
    if (!ok) return;
    setVoidingSale(true);
    try {
      await api().post(`/pos/sales/${blockingSale.id}/void`, {
        // The id, not a doc number: this form does not hold one (it is
        // allocated server-side), and the reason is an audit trail — it has to
        // point at something findable.
        reason: t('revise.posBlock.reason', {
          doc: id ?? '',
          defaultValue: 'Voided to correct invoice {{doc}}',
        }),
      });
      toast.success(t('revise.posBlock.voided', {
        sale: blockingSale.saleNumber,
        defaultValue: 'Sale {{sale}} and its invoice were voided. Ring the corrected sale at the register.',
      }));
      // Clear the blocker AND the sentence that named it: leaving either up
      // would have the screen still refusing something that is now allowed.
      setBlockingSale(null);
      setError(null);
    } catch (err) {
      setError(reviseErrorMessage(err, t, tc));
    } finally {
      setVoidingSale(false);
    }
  };

  /**
   * Settle a just-created bill via `POST /bills/:id/pay`, which posts the bill
   * and its outbound payment server-side under one lock (idempotent — a retry
   * on an already-settled bill is a no-op, never a double payment).
   *
   * The bill exists either way at this point, so on failure we still land on
   * its detail page and explain what did and did not happen, rather than
   * dropping the user back on a form whose document has already been created.
   */
  const settleNow = async (billId: string): Promise<void> => {
    try {
      const r = await api().post<{ amountPaid: string; currencyCode: string; alreadyPaid: boolean }>(
        `${endpoint}/${billId}/pay`,
        {
          method: payMethod || undefined,
          ...(payAccountId ? { cashAccountId: payAccountId } : {}),
          // Omitted for a full settlement, which is what this endpoint has always
          // meant. The server clamps a part payment to the outstanding balance.
          ...(!payFull && payAmount.trim() ? { amount: payAmount.trim() } : {}),
        },
      );
      toast.success(
        t('form.payNow.done', {
          amount: formatMoney(r.data.amountPaid, { locale: i18n.language, currency: r.data.currencyCode }),
          defaultValue: 'Purchase recorded and paid — {{amount}}.',
        }),
      );
    } catch (err) {
      // Structured reasons arrive on `cause.code`; only factory-level problems
      // (rate_not_found, auth.forbidden) surface as the top-level `code`.
      const data = (err as {
        response?: { data?: { code?: string; cause?: { code?: string; account?: string } } };
      }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      const unpaid = t('form.payNow.unpaid', { defaultValue: 'The purchase was saved but has not been paid.' });
      toast.error(
        code === 'currency.rate_not_found'
          ? `${tc('errors:currency.rate_not_found')} ${unpaid}`
          : code === 'auth.forbidden'
            ? t('form.payNow.forbidden', { defaultValue: 'The purchase was saved, but you cannot post payments.' })
            : code === 'gl.unconfigured_account'
              ? `${t('form.payNow.unconfigured', { account: data?.cause?.account ?? '', defaultValue: 'A general-ledger account ({{account}}) is not configured, so the purchase could not be posted.' })} ${unpaid}`
              : `${tc('errors:server')} ${unpaid}`,
        { duration: 10_000 },
      );
    }
    navigate(`${listPath}/${billId}`);
  };

  const fmt = (amt: string): string =>
    formatMoney(amt, { locale: i18n.language, currency: currencyCode });

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">
          {edit ? t('form.editTitle', { defaultValue: 'Edit document' }) : t('form.title')}
        </h1>
      </header>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Card>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <FormField id="d-date" label={t(docDateLabelKey ?? 'form.docDate')} required>
              <Input id="d-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} required />
            </FormField>
            {hasDueDate && (
              <FormField id="d-due" label={t(dueDateLabelKey ?? 'form.dueDate')}>
                <Input id="d-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </FormField>
            )}
            {isBill && (
              <FormField
                id="d-supref"
                label={t('form.supplierInvoiceNo', { defaultValue: 'Supplier invoice no.' })}
                helperText={t('form.supplierInvoiceNoHint', { defaultValue: 'The number printed on the supplier’s own invoice.' })}
              >
                <Input
                  id="d-supref"
                  value={supplierInvoiceNo}
                  onChange={(e) => setSupplierInvoiceNo(e.target.value)}
                  maxLength={64}
                  dir="ltr"
                  className="font-mono"
                />
              </FormField>
            )}
            <FormField id="d-currency" label={t('form.currency')} required>
              <CurrencySelect id="d-currency" value={currencyCode} onChange={onCurrencyChange} required />
            </FormField>
            <FormField id="d-party" label={t(`form.party.${partySide}`)} required>
              <Combobox
                id="d-party"
                options={partyOptions}
                onSearch={(q) => searchRemote('party', q)}
                value={partyId || undefined}
                // Selecting a customer only records WHO: the effect above then
                // re-prices the header currency and every line together, from the
                // server. Re-pricing from here read a currency one render stale.
                onChange={(v) => setPartyId(v ?? '')}
                placeholder={t('form.searchParty', { defaultValue: 'Search…' })}
                ariaLabel={t(`form.party.${partySide}`)}
              />
            </FormField>
            {hasWarehouse && warehouses.length > 0 && (
              <FormField
                id="d-warehouse"
                label={t('form.warehouse', { defaultValue: 'Warehouse' })}
                helperText={endpoint === '/bills'
                  ? t('form.warehouseHintBill', { defaultValue: 'Posting receives the items into this warehouse.' })
                  : endpoint === '/invoices'
                    ? t('form.warehouseHintInvoice', { defaultValue: 'Posting ships the items out of this warehouse.' })
                    : endpoint === '/sales-orders'
                      ? t('form.warehouseHintSo', { defaultValue: 'Carried onto the invoice when the order is converted.' })
                      : t('form.warehouseHintPo', { defaultValue: 'Carried onto the bill when the order is converted.' })}
              >
                <Combobox
                  id="d-warehouse"
                  options={warehouses.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))}
                  value={warehouseId || undefined}
                  onChange={(v) => setWarehouseId(v ?? '')}
                  ariaLabel={t('form.warehouse', { defaultValue: 'Warehouse' })}
                />
              </FormField>
            )}
          </div>
          {/* Which customer group prices this document — and in which currency.
              The pill turns amber when the header currency was overridden away
              from the group's, since the group's prices then can't be applied. */}
          {pricingOn && basis?.priceListId && (
            <p className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
              <Tags className="h-3.5 w-3.5 flex-none" aria-hidden />
              <span>{tc('pricing:assign.label', { defaultValue: 'Price list' })}</span>
              <span className="rounded-full border border-border bg-bg-subtle px-2 py-0.5 font-medium text-fg">
                {basis.priceListName}
              </span>
              {basis.currencyCode && (
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 font-mono',
                    basis.currencyCode === currencyCode
                      ? 'border-border bg-bg-subtle text-fg'
                      : 'border-warning/40 bg-warning/10 text-warning',
                  )}
                >
                  {basis.currencyCode}
                </span>
              )}
            </p>
          )}
          {partySide === 'customer' && partyId && (
            <div className="mt-4 border-t border-border pt-4">
              {balanceLoading && !balance ? (
                <Skeleton className="h-6 w-48" />
              ) : (
                (() => {
                  const cur = (balance ?? []).find((b) => b.currencyCode === currencyCode);
                  const others = (balance ?? []).filter(
                    (b) => b.currencyCode !== currencyCode && Number(b.balance) !== 0,
                  );
                  const bal = Number(cur?.balance ?? '0');
                  const owes = bal > 0;
                  const credit = bal < 0;
                  return (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-fg-subtle">
                        {t('form.balance.label', { defaultValue: 'Current balance' })}
                      </span>
                      <span
                        className={cn(
                          'font-mono text-lg font-extrabold tabular-nums',
                          owes ? 'text-warning' : credit ? 'text-success' : 'text-fg',
                        )}
                      >
                        {fmt(cur?.balance ?? '0')}
                      </span>
                      <span className="text-xs text-fg-muted">
                        {owes
                          ? t('form.balance.owes', { defaultValue: 'outstanding — owed by customer' })
                          : credit
                            ? t('form.balance.credit', { defaultValue: 'in credit' })
                            : t('form.balance.settled', { defaultValue: 'settled' })}
                      </span>
                      {others.length > 0 && (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-xs text-fg-subtle">
                            {t('form.balance.otherCurrencies', { defaultValue: 'Other currencies:' })}
                          </span>
                          {others.map((o) => (
                            <span
                              key={o.currencyCode}
                              className="rounded-full border border-border bg-bg-subtle px-2 py-0.5 font-mono text-xs"
                            >
                              {formatMoney(o.balance, { locale: i18n.language, currency: o.currencyCode })}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </Card>

        {/* Scan a barcode or search the catalogue to add a line. */}
        <div className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2">
          <ScanLine className="h-5 w-5 flex-none text-fg-muted" aria-hidden />
          <div className="flex-1">
            <Combobox
              options={scanOptions}
              onSearch={(q) => searchRemote('item', q)}
              onChange={(v) => addItemLine(v)}
              onEnterUnmatched={(code) => void resolveBarcode(code)}
              clearOnSelect
              placeholder={t('form.scanAdd', { defaultValue: 'Scan barcode or search to add an item…' })}
              ariaLabel={t('form.scanAdd', { defaultValue: 'Scan barcode or search to add an item…' })}
            />
          </div>
        </div>

        <Card className="p-0">
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-start">{t('form.line.item')}</th>
                <th className="px-3 py-2 text-start w-32">{t('form.line.unit', { defaultValue: 'Unit' })}</th>
                <th className="px-3 py-2 text-start">{t('form.line.description')}</th>
                <th className="px-3 py-2 text-end w-20">{t('form.line.qty')}</th>
                <th className="px-3 py-2 text-end w-28">{t('form.line.unitPrice')}</th>
                {taxOn && <th className="px-3 py-2 text-end w-20">{t('form.line.taxRate')}</th>}
                <th className="px-3 py-2 text-end w-28">{t('form.line.total')}</th>
                {showPublish && (
                  <th className="w-20 px-2 py-2 text-center">
                    <label className="inline-flex cursor-pointer flex-col items-center gap-0.5" title={t('form.publish.hint', { defaultValue: 'Show these products on the online store' })}>
                      <span>{t('form.publish.column', { defaultValue: 'Publish' })}</span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={publishAll}
                        ref={(el) => { if (el) el.indeterminate = publishSome && !publishAll; }}
                        disabled={publishable.length === 0}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setPublishWant((m) => ({ ...m, ...Object.fromEntries(publishable.map((iid) => [iid, v])) }));
                        }}
                        aria-label={t('form.publish.all', { defaultValue: 'Publish all' })}
                      />
                    </label>
                  </th>
                )}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const qty = safeDecimal(line.quantity);
                const price = safeDecimal(line.unitPrice);
                const rate = safeDecimal(line.taxRate);
                const lineTotal = qty.times(price).times(new Decimal(1).plus(rate)).toFixed(4);
                const lineItem = line.itemId ? items.find((it) => it.id === line.itemId) : undefined;
                const lineUnits = lineItem?.units ?? [];
                return (
                  <tr key={line._key} className="border-t border-border">
                    <td className="px-3 py-2 min-w-[220px]">
                      <Combobox
                        size="sm"
                        options={itemOptions}
                        onSearch={(q) => searchRemote('item', q)}
                        value={line.itemId}
                        onChange={(v) => onItemPicked(idx, v ?? '')}
                        placeholder={t('form.searchItem', { defaultValue: 'Search item or barcode…' })}
                        ariaLabel={t('form.line.item')}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {lineUnits.length > 0 ? (
                        <select
                          className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg"
                          value={line.itemUnitId ?? ''}
                          onChange={(e) => onUnitPicked(idx, line.itemId, e.target.value)}
                          aria-label={t('form.line.unit', { defaultValue: 'Unit' })}
                        >
                          <option value="">
                            {lineItem?.unitOfMeasure ?? t('form.line.baseUnit', { defaultValue: 'Base' })}
                          </option>
                          {lineUnits.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} (×{trimMoney(u.factor)})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-fg-muted">{lineItem?.unitOfMeasure ?? '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Input value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
                      {(() => {
                        // What this product SELLS at, in the line's own unit — and the
                        // product's price editor one click away (a new tab, so the bill
                        // being keyed is not lost; its prices refresh on coming back).
                        const ctx = isPurchaseDoc && line.itemId ? buyCtx[line.itemId] : undefined;
                        if (!ctx || !line.itemId) return null;
                        const factor = new Decimal(lineUnits.find((u) => u.id === line.itemUnitId)?.factor ?? 1);
                        const inUnit = (base: string | null, units: Record<string, string | null>): Decimal | null => {
                          const own = line.itemUnitId ? units[line.itemUnitId] : undefined;
                          if (own != null) return new Decimal(own);
                          return base != null ? new Decimal(base).times(factor) : null;
                        };
                        const retail = inUnit(ctx.retail.base, ctx.retail.units);
                        const parts = [
                          ...(retail ? [formatMoney(retail.toFixed(), { locale: i18n.language, currency: ctx.retail.currencyCode ?? baseCurrency })] : []),
                          ...ctx.lists.flatMap((l) => {
                            const p = inUnit(l.base, l.units);
                            return p ? [`${l.name} ${formatMoney(p.toFixed(), { locale: i18n.language, currency: l.currencyCode })}`] : [];
                          }),
                        ];
                        return (
                          <a
                            href={appUrl(`/inventory/items/${line.itemId}?tab=prices`)}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-fg-muted hover:text-brand"
                            title={t('form.buy.editPrices', { defaultValue: "Open this product's prices" })}
                          >
                            <span className="font-medium">{t('form.buy.sells', { defaultValue: 'Sells' })}:</span>
                            <span className="truncate">{parts.length ? parts.join(' · ') : '—'}</span>
                            <ExternalLink className="h-3 w-3 flex-none" aria-hidden />
                          </a>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      {/* The blur handler reads the LINE STATE, not e.target.value: the
                          event's target carries the grouped display ("1,000"), and
                          applyResolvedPrice parses what it is handed. Reaching for the
                          event here is exactly how a separator gets into a price. */}
                      <NumberInput
                        className="text-end font-mono"
                        value={line.quantity}
                        onValueChange={(raw) => updateLine(idx, { quantity: raw })}
                        onBlur={() => void applyResolvedPrice(line._key, line.itemId, line.quantity, partyId, line.itemUnitId)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <NumberInput
                        className="text-end font-mono"
                        value={line.unitPrice}
                        onValueChange={(raw) => updateLine(idx, { unitPrice: raw })}
                        // A unit price keeps its own precision (the API takes 4 places):
                        // $0.2121 a piece, rounded to cents, moved every total below it.
                        maxDecimals={4}
                      />
                      {(() => {
                        // «آخر سعر»: what this product was last bought at, in THIS line's
                        // unit, and whether the price being typed is dearer or cheaper.
                        const lp = isPurchaseDoc && line.itemId ? buyCtx[line.itemId]?.lastPurchase : undefined;
                        if (!lp) return null;
                        const factor = new Decimal(lineUnits.find((u) => u.id === line.itemUnitId)?.factor ?? 1);
                        const last = new Decimal(lp.perBase).times(factor).toDecimalPlaces(4);
                        const typed = safeDecimal(line.unitPrice);
                        const pct = lp.currencyCode === currencyCode && last.gt(0) && typed.gt(0)
                          ? typed.minus(last).dividedBy(last).times(100)
                          : null;
                        const tone = pct === null || pct.abs().lt(0.5)
                          ? 'text-fg-muted'
                          : pct.gt(0) ? 'text-danger' : 'text-success';
                        return (
                          <div
                            className={`mt-1 text-end text-xs tabular-nums ${tone}`}
                            title={t('form.buy.lastHint', {
                              doc: lp.docNumber, date: lp.docDate, party: lp.partyName ?? '',
                              defaultValue: '{{doc}} · {{date}} · {{party}}',
                            })}
                          >
                            {t('form.buy.last', { defaultValue: 'Last bought' })}: {trimMoney(last.toFixed())} {lp.currencyCode}
                            {pct !== null && !pct.abs().lt(0.5) && (
                              <span className="ms-1 font-semibold">
                                {pct.gt(0) ? '▲' : '▼'} {pct.gt(0) ? '+' : ''}{pct.toFixed(0)}%
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    {taxOn && (
                      <td className="px-3 py-2">
                        <Input
                          className="text-end font-mono"
                          value={line.taxRate}
                          onChange={(e) => updateLine(idx, { taxRate: e.target.value })}
                          inputMode="decimal"
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 text-end font-mono">{fmt(lineTotal)}</td>
                    {showPublish && (
                      <td className="px-2 py-2 text-center">
                        {line.itemId && publishKnown[line.itemId]?.sellable ? (
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={publishWant[line.itemId] ?? false}
                            onChange={(e) => {
                              const iid = line.itemId!;
                              const v = e.target.checked;
                              setPublishWant((m) => ({ ...m, [iid]: v }));
                            }}
                            aria-label={t('form.publish.column', { defaultValue: 'Publish' })}
                          />
                        ) : (
                          <span className="text-fg-subtle">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-end">
                      {lines.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(idx)}>×</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2">
            <Button type="button" variant="secondary" size="sm" onClick={addLine}>
              + {t('form.line.add')}
            </Button>
            {/* The old system's footer: how many lines, and the quantity column added up. */}
            <span className="text-xs tabular-nums text-fg-muted">
              {t('form.buy.summary', {
                lines: lines.filter((l) => l.itemId || l.description.trim()).length,
                qty: trimMoney(lines.reduce((a, l) => a.plus(safeDecimal(l.quantity)), new Decimal(0)).toFixed()),
                defaultValue: '{{lines}} lines · total quantity {{qty}}',
              })}
            </span>
          </div>
        </Card>

        <Card>
          {showDiscount && (
            <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
              {/* coupon — takes over the discount when applied */}
              {couponApplied ? (
                <span
                  className={
                    'inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm ' +
                    (Number(couponApplied.amount) > 0
                      ? 'border-success/40 bg-success/10'
                      : 'border-border bg-bg-subtle')
                  }
                >
                  <Ticket className="h-4 w-4" aria-hidden />
                  <span className="font-medium">{couponApplied.code}</span>
                  {Number(couponApplied.amount) === 0 && (
                    <span className="text-xs font-normal text-fg-muted">
                      · {t('form.coupon.noDiscount', { defaultValue: 'no discount' })}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={removeCoupon}
                    aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                    className="text-fg-muted hover:text-danger"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              ) : (
                <FormField id="d-coupon" label={t('form.coupon.label', { defaultValue: 'Coupon' })}>
                  <div className="flex gap-1.5">
                    <Input
                      id="d-coupon"
                      className="w-40 font-mono"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); void applyCoupon(); }
                      }}
                      placeholder={t('form.coupon.placeholder', { defaultValue: 'Code' })}
                    />
                    <Button type="button" variant="secondary" size="sm" onClick={applyCoupon} disabled={!couponCode.trim()}>
                      {t('form.coupon.apply', { defaultValue: 'Apply' })}
                    </Button>
                  </div>
                </FormField>
              )}
              {/* manual discount — only when no coupon is applied (mutually exclusive) */}
              {!couponApplied && (
                <div className="flex flex-wrap items-end gap-3">
                  <FormField id="d-disc-type" label={t('form.discount.label', { defaultValue: 'Discount' })}>
                    <select
                      id="d-disc-type"
                      className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value as 'none' | 'percent' | 'fixed')}
                    >
                      <option value="none">{t('form.discount.none', { defaultValue: 'No discount' })}</option>
                      <option value="percent">{t('form.discount.percent', { defaultValue: 'Percentage (%)' })}</option>
                      <option value="fixed">{t('form.discount.fixed', { defaultValue: 'Fixed amount' })}</option>
                    </select>
                  </FormField>
                  {discountType !== 'none' && (
                    <FormField
                      id="d-disc-val"
                      label={discountType === 'percent'
                        ? t('form.discount.percentValue', { defaultValue: 'Percent off' })
                        : t('form.discount.amountValue', { defaultValue: 'Amount off' })}
                    >
                      {/* The SAME box is an amount or a percentage depending on the
                          select above it, so grouping follows the selector — "15%"
                          must never render as a grouped amount. The component already
                          strips anything that is not a digit or a point, so the
                          hand-rolled sanitiser goes; the 4-decimal clamp stays. */}
                      <NumberInput
                        id="d-disc-val"
                        className="w-28 text-end font-mono"
                        value={discountValue}
                        grouped={discountType === 'fixed'}
                        onValueChange={(raw) => setDiscountValue(raw.replace(/^(\d*\.\d{0,4}).*$/, '$1'))}
                      />
                    </FormField>
                  )}
                </div>
              )}
            </div>
          )}
          <table className="w-full text-end text-sm">
            <tbody>
              <tr>
                <td className="px-4 py-1 text-fg-muted">{t('form.totals.subtotal')}</td>
                <td className="px-4 py-1 font-mono">{fmt(totals.subtotal)}</td>
              </tr>
              {Number(totals.discount) > 0 && (
                <tr>
                  <td className="px-4 py-1 text-fg-muted">{t('form.totals.discount', { defaultValue: 'Discount' })}</td>
                  <td className="px-4 py-1 font-mono text-danger">−{fmt(totals.discount)}</td>
                </tr>
              )}
              {Number(totals.tax) > 0 && (
                <tr>
                  <td className="px-4 py-1 text-fg-muted">{t('form.totals.tax')}</td>
                  <td className="px-4 py-1 font-mono">{fmt(totals.tax)}</td>
                </tr>
              )}
              {/* The delivery charge the order already carries: shown so the
                  total reconciles, not editable — it is the shop's own fee for
                  the city the customer chose, not a product on the order. */}
              {Number(totals.carried) > 0 && (
                <tr>
                  <td className="px-4 py-1 text-fg-muted">
                    {t('form.totals.delivery', { defaultValue: 'Delivery' })}
                  </td>
                  <td className="px-4 py-1 font-mono">{fmt(totals.carried)}</td>
                </tr>
              )}
              <tr className="border-t border-border">
                <td className="px-4 py-2 text-base font-semibold">{t('form.totals.total')}</td>
                <td className="px-4 py-2 font-mono text-base font-semibold">{fmt(totals.total)}</td>
              </tr>
            </tbody>
          </table>
        </Card>

        {supportsPayNow && (
          <Card>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[var(--color-brand)]"
                checked={payNow}
                onChange={(e) => setPayNow(e.target.checked)}
              />
              <span>
                <span className="flex items-center gap-2 text-sm font-medium text-fg">
                  <Wallet className="h-4 w-4 text-brand" aria-hidden />
                  {t('form.payNow.label', { defaultValue: 'Paid now (cash purchase)' })}
                </span>
                <span className="mt-0.5 block text-xs text-fg-muted">
                  {t('form.payNow.hint', {
                    defaultValue: 'Approves this purchase and pays it in full straight away. Leave it off to buy on credit.',
                  })}
                </span>
              </span>
            </label>

            {/* How much of it is being handed over now. A shop paying a supplier at the
                counter very often pays some of the bill and owes the rest — settling in
                full was the only thing this form could say. */}
            {payNow && (
              <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-4">
                <span className="inline-flex overflow-hidden rounded-md border border-border">
                  <button
                    type="button"
                    aria-pressed={payFull}
                    onClick={() => setPayFull(true)}
                    className={`px-3 py-1.5 text-xs ${payFull ? 'bg-brand text-white' : 'bg-bg text-fg-muted'}`}
                  >
                    {t('form.payNow.full', { defaultValue: 'Pay in full' })}
                  </button>
                  <button
                    type="button"
                    aria-pressed={!payFull}
                    onClick={() => {
                      setPayFull(false);
                      // Seed the box with the total, so the number is a starting point to
                      // edit down rather than an empty field to work out from scratch.
                      if (!payAmount.trim()) setPayAmount(totals.total);
                    }}
                    className={`px-3 py-1.5 text-xs ${!payFull ? 'bg-brand text-white' : 'bg-bg text-fg-muted'}`}
                  >
                    {t('form.payNow.part', { defaultValue: 'Pay part' })}
                  </button>
                </span>
                {!payFull && (
                  <FormField
                    id="d-pay-amount"
                    label={t('form.payNow.amount', { defaultValue: 'Amount paid now' })}
                    helperText={t('form.payNow.amountHint', {
                      total: fmt(totals.total),
                      defaultValue: 'The rest stays owed to the supplier. Total is {{total}}.',
                    })}
                  >
                    <NumberInput
                      id="d-pay-amount"
                      value={payAmount}
                      onValueChange={setPayAmount}
                      currency={currencyCode}
                      className="font-mono"
                    />
                  </FormField>
                )}
              </div>
            )}

            {payNow && (
              <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 md:grid-cols-2">
                <FormField id="d-pay-method" label={t('form.payNow.method', { defaultValue: 'Paid by' })}>
                  <select
                    id="d-pay-method"
                    className="h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg"
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value)}
                  >
                    <option value="cash">{t('form.payNow.methods.cash', { defaultValue: 'Cash' })}</option>
                    <option value="bank">{t('form.payNow.methods.bank', { defaultValue: 'Bank transfer' })}</option>
                    <option value="card">{t('form.payNow.methods.card', { defaultValue: 'Card' })}</option>
                    <option value="cheque">{t('form.payNow.methods.cheque', { defaultValue: 'Cheque' })}</option>
                  </select>
                </FormField>
                <FormField
                  id="d-pay-account"
                  label={t('form.payNow.account', { defaultValue: 'Paid from' })}
                  helperText={t('form.payNow.accountHint', { defaultValue: 'Leave empty to use the default cash account.' })}
                >
                  <Combobox
                    id="d-pay-account"
                    options={(cashAccounts ?? []).map((a): ComboboxOption => ({
                      value: a.id,
                      label: `${a.code} — ${a.name}`,
                      keywords: `${a.code} ${a.name}`,
                    }))}
                    value={payAccountId || undefined}
                    onChange={(v) => setPayAccountId(v ?? '')}
                    placeholder={t('form.payNow.accountDefault', { defaultValue: 'Default cash account' })}
                    ariaLabel={t('form.payNow.account', { defaultValue: 'Paid from' })}
                  />
                </FormField>
              </div>
            )}
          </Card>
        )}

        <FormField id="d-notes" label={t('form.notes')}>
          <textarea
            id="d-notes"
            className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormField>

        {postedEntry !== null && (
          <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-fg" role="status">
            {t('revise.editBanner', {
              entry: postedEntry,
              defaultValue: 'This document is already posted. Saving will reverse journal entry {{entry}} and re-post it with the new amounts.',
            })}
          </p>
        )}

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        {/* The register sale that refused this edit, and the way past it.
            A POS sale owns its own undo — voiding one refunds the tender and
            puts the stock back — which is exactly why the invoice it produced
            cannot be quietly rewritten around it. So this offers that undo
            rather than a bypass: nothing here skips a check, it performs the
            one the refusal is asking for, without leaving the page. */}
        {blockingSale && (
          <div className="flex flex-col gap-3 rounded-lg border border-danger/40 bg-danger/5 p-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="text-fg-muted">{t('revise.posBlock.label', { defaultValue: 'Register sale:' })}</span>
              <Link to={`/pos/sales?sale=${blockingSale.id}`} className="font-mono font-semibold text-brand hover:underline">
                {blockingSale.saleNumber}
              </Link>
            </div>
            <p className="text-xs text-fg-muted">
              {canVoidPosSale
                ? t('revise.posBlock.hint', {
                  defaultValue: 'This invoice was raised by the register, and cannot be edited. Voiding the sale refunds what was tendered, puts the stock back and cancels this invoice — then ring the correct sale again.',
                })
                : t('revise.posBlock.noPermission', {
                  defaultValue: 'Only someone who can refund at the till may undo it. Ask them to void this sale first.',
                })}
            </p>
            {canVoidPosSale && (
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  loading={voidingSale}
                  onClick={() => void voidBlockingSale()}
                >
                  {t('revise.posBlock.void', { defaultValue: 'Void the register sale' })}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button type="submit" loading={saving}>{t('form.submit')}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate(listPath)} disabled={saving}>
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
}
